import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface PaginationModel {
  page: number;
  pageSize: number;
}

interface CallsPageState {
  selectedAgent: any;
  /** Persists selected call on Call Audits page so audit state survives navigation */
  selectedCallIdForAudit: string | null;
  fromDate: string;
  toDate: string;
  paginationModel: PaginationModel;
  // DataGrid filter model to persist column filters
  filterModel: any;
  // DataGrid sort model to persist sorting
  sortModel: any;
}

const getDefaultDateRange = () => {
  const today = new Date();
  const lastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate()
  );

  const formatDate = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  return {
    fromDate: formatDate(lastMonth),
    toDate: formatDate(today),
  };
};

const { fromDate, toDate } = getDefaultDateRange();

const initialState: CallsPageState = {
  selectedAgent: null,
  selectedCallIdForAudit: null,
  fromDate,
  toDate,
  paginationModel: {
    page: 0,
    pageSize: 100,
  },
  filterModel: {
    items: [],
  },
  sortModel: [],
};

const callsPageSlice = createSlice({
  name: 'callsPage',
  initialState,
  reducers: {
    setSelectedAgent: (state, action: PayloadAction<any>) => {
      state.selectedAgent = action.payload;
    },
    setSelectedCallIdForAudit: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedCallIdForAudit = action.payload;
    },
    setFromDate: (state, action: PayloadAction<string>) => {
      state.fromDate = action.payload;
      // Reset pagination when date filter changes
      state.paginationModel.page = 0;
    },
    setToDate: (state, action: PayloadAction<string>) => {
      state.toDate = action.payload;
      // Reset pagination when date filter changes
      state.paginationModel.page = 0;
    },
    setPaginationModel: (state, action: PayloadAction<PaginationModel>) => {
      state.paginationModel = action.payload;
    },
    setFilterModel: (state, action: PayloadAction<any>) => {
      state.filterModel = action.payload;
      // Reset pagination when filters change
      state.paginationModel.page = 0;
    },
    setSortModel: (state, action: PayloadAction<any>) => {
      state.sortModel = action.payload;
    },
    resetCallsPageState: state => {
      // Keep selected agent but reset other filters
      state.fromDate = fromDate;
      state.toDate = toDate;
      state.paginationModel = {
        page: 0,
        pageSize: 100,
      };
      state.filterModel = {
        items: [],
      };
      state.sortModel = [];
    },
  },
});

export const {
  setSelectedAgent,
  setSelectedCallIdForAudit,
  setFromDate,
  setToDate,
  setPaginationModel,
  setFilterModel,
  setSortModel,
  resetCallsPageState,
} = callsPageSlice.actions;

export default callsPageSlice.reducer;
